import { Directive, forwardRef } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

/**
 * @description
 * Validates that a date of birth is within acceptable range:
 * - Maximum date: today
 * - Minimum date: January 1, 1900
 * 
 * @example
 * ```html
 * <input type="date"
 *        class="input"
 *        name="dob"
 *        required
 *        validateDobRange
 *        [(ngModel)]="dob"
 *        [muiInput]="'Date of birth' | translate">
 * ```
 */
@Directive({
    selector: '[validateDobRange]',
    standalone: true,
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ValidateDobRangeDirective),
            multi: true
        }
    ]
})
export class ValidateDobRangeDirective implements Validator {
    private readonly minDate = new Date('1900-01-01');
    private readonly maxDate = new Date();

    /**
     * @description
     * Method that validates whether the date of birth is within acceptable range.
     * Returns validation errors if date is outside range, otherwise null.
     *
     * @param {AbstractControl} control
     * @returns {ValidationErrors | null}
     */
    validate(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // Let required validator handle empty values
        }

        const inputDate = new Date(control.value);
        
        // Check if date is valid
        if (isNaN(inputDate.getTime())) {
            return { validateDobRange: { message: 'Invalid date format' } };
        }

        // Check if date is too early (before 1900)
        if (inputDate < this.minDate) {
            return { 
                validateDobRange: { 
                    message: 'Date of birth cannot be before January 1, 1900',
                    minDate: this.minDate.toISOString().split('T')[0]
                } 
            };
        }

        // Check if date is in the future (after today)
        if (inputDate > this.maxDate) {
            return { 
                validateDobRange: { 
                    message: 'Date of birth cannot be in the future',
                    maxDate: this.maxDate.toISOString().split('T')[0]
                } 
            };
        }

        return null;
    }
}
